/**
 * Base HTTP Client
 *
 * Type-safe HTTP client with retry, error handling, and request interceptors.
 */

import { SdkConfig } from './config';
import { TonnieApiError, apiErrorFrom } from './api-error';
import { xhrUpload, type UploadProgress } from './upload';

export {
  TonnieApiError,
  apiErrorFrom,
  destinationNotOpenOf,
  isAiUnavailable,
  type ApiError,
} from './api-error';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface RequestOptions {
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  retry?: number;
  timeout?: number;
  /**
   * Response parsing mode. Default `'json'`. Use `'blob'` for binary downloads
   * (e.g. a ZIP / PDF export) — the method then resolves to a `Blob` instead of
   * parsed JSON. The auth header + interceptors still apply.
   */
  responseType?: 'json' | 'blob';
  /**
   * Only meaningful with `responseType: 'blob'` (D7). `fetch`'s `Response` is
   * otherwise discarded once `.blob()` is read, so a server-chosen filename in
   * `Content-Disposition` — e.g. cohort CSV export's `-partial` truncation
   * suffix — never reaches the caller; today every blob download rebuilds its
   * own filename client-side and silently drops that signal. When `true`, the
   * method resolves to `{ blob, filename }` instead of a bare `Blob` —
   * `filename` is the disposition's `filename`/`filename*` parameter, or
   * `null` if the header is absent or unparseable. Opt-in (not the default for
   * every blob caller) so the other blob downloads keep their existing
   * `Promise<Blob>` return type unchanged.
   */
  withFilename?: boolean;
}

export interface BlobWithFilename {
  blob: Blob;
  /** From `Content-Disposition`'s `filename`/`filename*` param, or `null`. */
  filename: string | null;
}

/**
 * `Content-Disposition: attachment; filename="x-partial.csv"` or the RFC 5987
 * extended form `filename*=UTF-8''x%2Dpartial.csv`. Prefers `filename*` (the
 * one place a non-ASCII server-chosen name survives) and falls back to the
 * plain form. Returns `null` rather than throwing on anything unparseable —
 * a filename is a nicety, never worth failing the download over.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * Options for an upload (`postForm` / `putPresigned`).
 *
 * `timeout` is deliberately omitted: a transfer is bounded by a stall window,
 * not by a deadline. Leaving `timeout` accepted-but-ignored is what let the
 * onboarding CV path carry an explicit `{ timeout: 60_000 }` that looked like a
 * policy and was a wall clock.
 */
export interface UploadOptions extends Pick<RequestOptions, 'params' | 'headers' | 'signal'> {
  /** ms with no upload progress before the transfer is abandoned. */
  stallTimeoutMs?: number;
  onProgress?: (progress: UploadProgress) => void;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

/**
 * Detect whether an arbitrary thrown value is a transport-level network
 * failure — the generic `TypeError: Failed to fetch` that browsers and
 * RN throw for CORS block / DNS / offline / TLS. Kept as a module-level
 * helper so both `executeRequest` + `postForm` can share the same
 * classification (single source of truth).
 */
function isTransportNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError') return false; // handled separately
  // Browsers: `TypeError: Failed to fetch` / `TypeError: NetworkError when attempting to fetch resource.`
  // React Native: `TypeError: Network request failed`
  // Node 18+ / undici: `TypeError: fetch failed` with `cause: { code: 'ENOTFOUND' | 'ECONNREFUSED' | ... }`
  if (error.name === 'TypeError') return true;
  const msg = error.message ?? '';
  return (
    msg.includes('Network request failed') ||
    msg.includes('Failed to fetch') ||
    msg.includes('fetch failed') ||
    msg.includes('NetworkError')
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CLIENT
// ═══════════════════════════════════════════════════════════════════════

export class TonnieClient {
  private config: SdkConfig;
  private authToken: string | null = null;
  private tokenProvider: (() => Promise<string | null>) | null = null;
  private localeProvider: (() => string | null | undefined) | null = null;
  private requestInterceptors: Array<(config: RequestInit) => RequestInit> = [];
  private responseInterceptors: Array<(response: Response) => Response> = [];

  constructor(config: SdkConfig) {
    this.config = config;
    this.authToken = config.authToken ?? null;
  }

  /**
   * Set the auth token for subsequent requests
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  /**
   * Set a token provider function that returns a fresh token before each request.
   * When set, this takes precedence over the static authToken.
   * Clerk's getToken() automatically handles refresh, so every API call
   * receives a valid, non-expired session token.
   */
  setTokenProvider(provider: (() => Promise<string | null>) | null): void {
    this.tokenProvider = provider;
  }

  /**
   * Set a provider returning the viewer's active UI locale. When it resolves to
   * a non-EN locale, requests carry an `x-locale` header so platform-api can
   * translate platform-owned content on read (BL-202). EN (or no provider) is a
   * no-op — no header, no translation cost. Apps wire this from `useLocale()`.
   */
  setLocaleProvider(provider: (() => string | null | undefined) | null): void {
    this.localeProvider = provider;
  }

  /**
   * Get current auth token
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Returns the configured base URL. Used by callers (e.g. the SSE
   * fetch-stream in `useTalentActivityStream`) that need to construct
   * request URLs without going through the fetch helpers.
   */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  /**
   * Resolve a fresh auth token the same way `executeRequest` does:
   * prefer the token provider (Clerk `getToken()` — handles refresh),
   * fall back to the static token. Used by long-lived streams (SSE via
   * fetch) that attach `Authorization` themselves (BL-017 — native
   * `EventSource` cannot send headers, which made the activity stream
   * 401 on Bearer-authenticated APIs).
   */
  async getFreshAuthToken(): Promise<string | null> {
    if (this.tokenProvider) {
      try {
        this.authToken = await this.tokenProvider();
      } catch {
        /* keep the previous token — same policy as executeRequest */
      }
    }
    return this.authToken;
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: (config: RequestInit) => RequestInit): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: (response: Response) => Response): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    const url = new URL(path, this.config.baseUrl);

    // Always inject API version
    url.searchParams.set('version', String(this.config.apiVersion));

    // Add additional params
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Get default headers
   */
  private getHeaders(additionalHeaders?: Record<string, string>): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...additionalHeaders,
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Add tenant ID if set
    if (this.config.tenantId) {
      headers['X-Tenant-Id'] = this.config.tenantId;
    }

    // Viewer locale for on-demand translation (BL-202). Only sent for non-EN so
    // EN readers stay a pure no-op end-to-end.
    const locale = this.localeProvider?.();
    if (locale && locale !== 'en') {
      headers['x-locale'] = locale;
    }

    return headers;
  }

  /**
   * Handle response
   */
  private async handleResponse<T>(
    response: Response,
    responseType?: 'json' | 'blob',
    withFilename?: boolean
  ): Promise<T> {
    // Apply response interceptors
    let interceptedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      interceptedResponse = interceptor(interceptedResponse);
    }

    // 401 still triggers the sign-out side effect, but it no longer
    // short-circuits the body parse below.
    if (interceptedResponse.status === 401) {
      this.config.onUnauthorized?.();
    }

    // ── ROOT CAUSE (H17): 401 and 403 used to throw HERE, before the body was
    // read, replacing the whole envelope with `{code:'FORBIDDEN', message:
    // 'Forbidden'}`. Every server-supplied machine code and human message on a
    // 403 was discarded, so `AcceptInvitePage`'s "Wrong account" branch — which
    // tests for TALENT_INVITATION_EMAIL_MISMATCH — could never match. A citizen
    // holding an invitation for a different address got a card titled
    // "Something went wrong" whose body was the literal word "Forbidden". One of
    // them retried three times, burned three invitations and deleted their
    // account.
    //
    // 4xx is never retried (see executeRequest), so parsing the body here adds
    // no retry surface. A non-JSON body still falls through the `.catch` below
    // to the status-family fallback, which is what 'Forbidden' used to be.
    if (!interceptedResponse.ok) {
      const errorBody = await interceptedResponse.json().catch(() => undefined);

      throw this.reportError(
        apiErrorFrom(interceptedResponse.status, interceptedResponse.statusText, errorBody)
      );
    }

    // Binary downloads (ZIP / PDF export) — return the raw Blob, not JSON.
    if (responseType === 'blob') {
      const blob = await interceptedResponse.blob();
      if (withFilename) {
        const filename = parseContentDispositionFilename(
          interceptedResponse.headers.get('content-disposition')
        );
        return { blob, filename } as T;
      }
      return blob as T;
    }

    // Handle empty responses (204 No Content, or 200 with empty body)
    if (interceptedResponse.status === 204) {
      return undefined as T;
    }

    const contentLength = interceptedResponse.headers.get('content-length');
    if (contentLength === '0') {
      return null as T;
    }

    const text = await interceptedResponse.text();
    if (!text || text.length === 0) {
      return null as T;
    }

    return JSON.parse(text);
  }

  /**
   * Execute request with retry logic
   */
  private async executeRequest<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    const url = this.buildUrl(path, options.params);
    const maxRetries = options.retry ?? this.config.retries ?? 0;
    const timeout = options.timeout ?? this.config.timeout ?? 30000;

    // Refresh the auth token via the provider before every request.
    // Clerk's getToken() returns a fresh JWT each time and handles
    // automatic refresh, so the request always carries a valid token.
    if (this.tokenProvider) {
      try {
        this.authToken = await this.tokenProvider();
      } catch {
        // If token refresh fails, proceed with the existing (possibly stale) token.
        // The server will return 401 and the caller can handle it.
      }
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        let requestConfig: RequestInit = {
          method,
          headers: this.getHeaders(options.headers),
          body: body ? JSON.stringify(body) : undefined,
          signal: options.signal ?? controller.signal,
        };

        // Apply request interceptors
        for (const interceptor of this.requestInterceptors) {
          requestConfig = interceptor(requestConfig);
        }

        const response = await fetch(url, requestConfig);
        clearTimeout(timeoutId);

        return await this.handleResponse<T>(response, options.responseType, options.withFilename);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on client errors (4xx) or if manually aborted
        if (error instanceof TonnieApiError && error.status < 500) {
          throw error;
        }

        // Don't retry if aborted
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.reportError(
            new TonnieApiError({
              message: 'Request timeout',
              status: 408,
              code: 'TIMEOUT',
            })
          );
        }

        // Transport-level network failures (CORS, DNS, offline, TLS).
        // Without this classification the caller sees a bare
        // `TypeError: Failed to fetch` and can't branch on it. On the
        // final attempt we rewrite it; inner attempts keep flowing so
        // the retry path can recover if the network blip clears.
        if (isTransportNetworkError(error) && attempt === maxRetries) {
          throw this.reportError(
            new TonnieApiError({
              message: 'Network request failed — unable to reach the server',
              status: 0,
              code: 'NETWORK_ERROR',
              details: {
                originalMessage: (error as Error).message,
                url,
                method,
              },
            })
          );
        }

        // Wait before retry with exponential backoff
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    // All retries exhausted with a non-TonnieApiError error — wrap it
    // as a server-error shape so callers still get a typed exception
    // with onError fired consistently.
    if (lastError && !(lastError instanceof TonnieApiError)) {
      throw this.reportError(
        new TonnieApiError({
          message: lastError.message || 'Request failed',
          status: 0,
          code: 'UNKNOWN_ERROR',
          details: { originalName: lastError.name },
        })
      );
    }
    throw lastError ?? new Error('Request failed');
  }

  /**
   * Fire the SdkConfig.onError hook and return the error so callers
   * can `throw this.reportError(...)` in a single expression. Intended
   * as the single choke-point for ALL API errors leaving the client —
   * makes sure Sentry / toast / breadcrumbs infra never misses a
   * failure.
   */
  private reportError(error: TonnieApiError): TonnieApiError {
    try {
      this.config.onError?.(error);
    } catch {
      // onError MUST NOT throw. If it does, swallow and propagate the
      // original error so request failures aren't masked by hook bugs.
    }
    return error;
  }

  /**
   * GET request
   */
  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.executeRequest<T>('GET', path, undefined, options);
  }

  /**
   * POST request
   */
  async post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.executeRequest<T>('POST', path, body, options);
  }

  /**
   * PUT request
   */
  async put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.executeRequest<T>('PUT', path, body, options);
  }

  /**
   * PATCH request
   */
  async patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.executeRequest<T>('PATCH', path, body, options);
  }

  /**
   * DELETE request. Supports an optional body (RFC 9110 allows this) —
   * useful for destructive operations that require a confirmation payload
   * (e.g. `{ confirmationName: "Acme Corp" }`) without leaking it into
   * URL query strings / access logs.
   */
  async delete<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.executeRequest<T>('DELETE', path, body, options);
  }

  /**
   * Run the registered request interceptors and return the headers they
   * produced. The interceptor contract is `(RequestInit) => RequestInit`, which
   * predates the XHR path; feeding them a synthetic init and reading the
   * headers back keeps ONE interceptor registration working for both
   * transports (the trace-id bridge is the only one today, and losing it on
   * uploads would break exactly the requests hardest to debug).
   */
  private applyInterceptorHeaders(base: Record<string, string>): Record<string, string> {
    let init: RequestInit = { headers: new Headers(base) };
    for (const interceptor of this.requestInterceptors) {
      init = interceptor(init);
    }
    const out: Record<string, string> = {};
    new Headers(init.headers).forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }

  /** Auth + tenant + locale headers for an authenticated upload. */
  private async uploadHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    if (this.tokenProvider) {
      try {
        this.authToken = await this.tokenProvider();
      } catch {
        // proceed with existing token — the server answers 401 and the caller
        // handles it, same policy as executeRequest.
      }
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...extra,
    };
    if (this.authToken) headers['Authorization'] = `Bearer ${this.authToken}`;
    if (this.config.tenantId) headers['X-Tenant-Id'] = this.config.tenantId;
    const locale = this.localeProvider?.();
    if (locale && locale !== 'en') headers['x-locale'] = locale;

    return this.applyInterceptorHeaders(headers);
  }

  /**
   * POST multipart/form-data (file uploads).
   *
   * Content-Type is deliberately NOT set — the engine sets it with the correct
   * multipart boundary for `FormData`.
   *
   * ORDERING (F39): the budget is resolved from `stallTimeoutMs` and NOTHING
   * else. `this.config.timeout` is not in the chain, and `options.timeout` is
   * not either — a wall-clock deadline is the wrong instrument for a transfer,
   * and while the general request timeout stayed in that chain it kept winning
   * (it is always populated, which is what made the old `?? 60000` unreachable
   * and every upload in three apps a 30-second deadline).
   */
  async postForm<T>(path: string, formData: FormData, options: UploadOptions = {}): Promise<T> {
    const url = this.buildUrl(path, options.params);
    const headers = await this.uploadHeaders(options.headers);

    const result = await xhrUpload<T>({
      method: 'POST',
      url,
      body: formData,
      headers,
      stallTimeoutMs: options.stallTimeoutMs,
      onProgress: options.onProgress,
      signal: options.signal,
    }).catch((error: unknown) => {
      if (error instanceof TonnieApiError) throw this.reportError(error);
      throw error;
    });

    return result;
  }

  /**
   * PUT bytes straight to a presigned storage URL.
   *
   * No auth header, no `?version=` param, no base URL: the URL is already
   * signed and any header that is not part of the signature is at best ignored
   * and at worst invalidates it. `Content-Type` MUST match the type the URL was
   * signed with, which is why it is a required argument rather than optional.
   */
  async putPresigned(
    uploadUrl: string,
    body: Blob | ArrayBufferView | ArrayBuffer,
    contentType: string,
    options: Pick<UploadOptions, 'onProgress' | 'signal' | 'stallTimeoutMs'> = {}
  ): Promise<void> {
    await xhrUpload<void>({
      method: 'PUT',
      url: uploadUrl,
      body,
      headers: { 'Content-Type': contentType },
      responseType: 'none',
      stallTimeoutMs: options.stallTimeoutMs,
      onProgress: options.onProgress,
      signal: options.signal,
    }).catch((error: unknown) => {
      if (error instanceof TonnieApiError) throw this.reportError(error);
      throw error;
    });
  }
}

export function createClient(config: SdkConfig): TonnieClient {
  return new TonnieClient(config);
}
